import { Button } from "@heroui/react";
import {
	type AdminEpisodeDetail,
	type AdminProductionProgress,
	ESTIMATED_IMAGE_COST_USD,
	ESTIMATED_SHOT_VIDEO_COST_USD,
	type GenerationFailureReason,
	type GenerationJob,
	type GenerationKind,
	type ProductionState,
} from "@storyline/shared";
import { useMemo, useState } from "react";
import ContactSummary from "@/src/stories/components/contact-summary";
import ContactVignette, { type ContactMode } from "@/src/stories/components/contact-vignette";
import CostProgressBanner from "@/src/stories/components/cost-progress-banner";
import { contactSheetStates, isShotVideoBusy, summarizeFrames } from "@/src/stories/utils/frame-state";
import { orderedShots } from "@/src/stories/utils/ordered-shots";

type ContactSheetProps = {
	episode: AdminEpisodeDetail;
	mode: ContactMode;
	production: AdminProductionProgress;
	generations: GenerationJob[];
	backState: ProductionState;
	nextState: ProductionState;
	nextLabel: string;
	onRegenerate: (shotId: string, kind: GenerationKind, reason: GenerationFailureReason) => void;
	onTransition: (to: ProductionState) => void;
};

const ContactSheet: React.FC<ContactSheetProps> = ({
	episode,
	mode,
	production,
	generations,
	backState,
	nextState,
	nextLabel,
	onRegenerate,
	onTransition,
}) => {
	const entries = useMemo(() => contactSheetStates(orderedShots(episode), generations), [episode, generations]);
	const summary = useMemo(() => summarizeFrames(entries.map((entry) => entry.state)), [entries]);
	const [approvedShotIds, setApprovedShotIds] = useState<Set<string>>(new Set());

	const toggleApprove = (shotId: string): void => {
		setApprovedShotIds((previous) => {
			const next = new Set(previous);
			if (next.has(shotId)) {
				next.delete(shotId);
			} else {
				next.add(shotId);
			}
			return next;
		});
	};

	const allApproved = entries.length > 0 && entries.every((entry) => approvedShotIds.has(entry.ordered.shot.id));

	return (
		<div className="flex flex-col gap-4">
			<CostProgressBanner production={production} />
			{mode === "keyframes" ? <ContactSummary summary={summary} /> : null}
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
				{entries.map((entry) => (
					<ContactVignette
						key={entry.ordered.shot.id}
						ordered={entry.ordered}
						state={entry.state}
						mode={mode}
						approved={approvedShotIds.has(entry.ordered.shot.id)}
						videoBusy={isShotVideoBusy(entry.ordered.shot.id, generations)}
						imageCostUsd={ESTIMATED_IMAGE_COST_USD}
						videoCostUsd={ESTIMATED_SHOT_VIDEO_COST_USD}
						onToggleApprove={() => toggleApprove(entry.ordered.shot.id)}
						onRegenerate={(kind, reason) => onRegenerate(entry.ordered.shot.id, kind, reason)}
					/>
				))}
			</div>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<Button variant="ghost" className="text-muted" onPress={() => onTransition(backState)}>
					← Étape précédente
				</Button>
				<Button isDisabled={!allApproved} onPress={() => onTransition(nextState)}>
					{nextLabel}
				</Button>
			</div>
		</div>
	);
};

export default ContactSheet;
